import { supabase } from './supabase';

export interface NotificationPreferences {
  email_enabled: boolean;
  email_tasks_deadline: boolean;
  email_budget_updates: boolean;
  email_daily_reminder: boolean;
  email_timer_reminder: boolean;
  email_project_updates: boolean;
  push_enabled: boolean;
  push_tasks_deadline: boolean;
  push_budget_updates: boolean;
  push_daily_reminder: boolean;
  push_timer_reminder: boolean;
  push_project_updates: boolean;
  inapp_enabled: boolean;
  daily_reminder_time: string;
  timer_reminder_minutes: number;
  deadline_reminder_hours: number;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('apro_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching notification preferences:', error);
      return null;
    }

    if (!data) {
      const { data: newPrefs, error: createError } = await supabase
        .from('apro_notification_preferences')
        .insert({ user_id: user.id })
        .select()
        .single();

      if (createError) {
        console.error('Error creating notification preferences:', createError);
        return null;
      }

      return newPrefs;
    }

    return data;
  } catch (error) {
    console.error('Error in getNotificationPreferences:', error);
    return null;
  }
}

export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    const { error } = await supabase
      .from('apro_notification_preferences')
      .update(preferences)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating notification preferences:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateNotificationPreferences:', error);
    return false;
  }
}

export async function getUnreadNotifications() {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('apro_notifications')
      .select('*')
      .eq('user_id', user.id)
      .eq('leido', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching unread notifications:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUnreadNotifications:', error);
    return [];
  }
}

export async function getAllNotifications(limit = 50) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('apro_notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllNotifications:', error);
    return [];
  }
}

export async function markNotificationAsRead(notificationId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('apro_notifications')
      .update({
        leido: true,
        leido_at: new Date().toISOString()
      })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in markNotificationAsRead:', error);
    return false;
  }
}

export async function markAllNotificationsAsRead(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    const { error } = await supabase
      .from('apro_notifications')
      .update({
        leido: true,
        leido_at: new Date().toISOString()
      })
      .eq('user_id', user.id)
      .eq('leido', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in markAllNotificationsAsRead:', error);
    return false;
  }
}

export async function deleteNotification(notificationId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('apro_notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteNotification:', error);
    return false;
  }
}

export async function scheduleNotification(
  tipo: string,
  templateKey: string,
  scheduledFor: Date,
  notificationData: Record<string, any>,
  isRecurring = false,
  recurrenceRule?: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    const { error } = await supabase
      .from('apro_notification_queue')
      .insert({
        user_id: user.id,
        tipo,
        template_key: templateKey,
        scheduled_for: scheduledFor.toISOString(),
        notification_data: notificationData,
        is_recurring: isRecurring,
        recurrence_rule: recurrenceRule,
      });

    if (error) {
      console.error('Error scheduling notification:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in scheduleNotification:', error);
    return false;
  }
}

export function subscribeToNotifications(
  callback: (notification: any) => void
): () => void {
  const channel = supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'apro_notifications',
      },
      (payload) => {
        callback(payload.new);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
